// ตารางเวร · ทันตแพทย์ — helpers for the doctor roster month grid + its editor.
//
// ⚠️ SOURCE OF TRUTH — read this before changing anything here.
// A doctor's shift is NOT a `doctor_timetable` row. That table (Xano id 28) is a
// dead Bubble import: 7,042 rows, none created since 2023-12-29, latest shift in
// Dec 2024. The roster the clinic actually runs on lives in the **`appointment`**
// table as rows with `event_type === 'inverse-background'` (35k+ rows, written
// every day) — the same rows `BookingCalendar` paints its month view from, and
// the same rows the booking day view uses as each doctor's working window.
// So this tab edits `appointment`, through /api/hr/schedule/doctor*.
//
// Row shape (one shift):
//   date        'YYYY-MM-DD'  Bangkok calendar day — authoritative, do not derive
//   start_date  epoch ms      window start   ·  end_date epoch ms  window end
//   duration    minutes       start→end, kept in sync by the writers
//   doctor_id / branch_id / titleDoctorTB (doctor.code, denormalised by Bubble)
//   event_color hex           usually the legacy grey #E0E0E0
//   details     free text     ·  all_day bool  ·  active bool (soft delete)
//
// Times are Asia/Bangkok and so is the browser, so LOCAL getters are the correct
// conversion — no offset arithmetic anywhere in this file.
import { parseDate } from './asset.js'

export { parseDate, toDateInput, fmtDateTH, relabelBranch } from './asset.js'

// The marker that makes an `appointment` row a doctor shift window rather than a
// patient booking. Never editable from the UI: flipping it would either hide the
// row from every roster feed or turn a shift into a phantom appointment.
export const SHIFT_EVENT_TYPE = 'inverse-background'

// What Bubble writes on a shift row. Meaningless as a label, but the booking app
// and the print views were built against it, so we match rather than improve.
export const SHIFT_TITLE = 'block'

// Legacy default. Rows carrying it are drawn in the neutral tone instead of a
// grey chip nobody chose — colour is opt-in.
export const DEFAULT_EVENT_COLOR = '#E0E0E0'

const pad = (n) => String(n).padStart(2, '0')

/* ── Thai calendar vocabulary ───────────────────────────────────────────── */
export const TH_MONTHS_FULL = ['มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน', 'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม']
export const TH_MONTHS_ABBR = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.']
export const TH_DAYS_FULL = ['อาทิตย์', 'จันทร์', 'อังคาร', 'พุธ', 'พฤหัสบดี', 'ศุกร์', 'เสาร์']
// Monday-first — the clinic's week starts Monday, and a Sunday-first grid puts
// the two quiet days at opposite ends of the row.
export const WEEK_HEAD = ['จันทร์', 'อังคาร', 'พุธ', 'พฤหัส', 'ศุกร์', 'เสาร์', 'อาทิตย์']

/* ── day keys + month arithmetic ────────────────────────────────────────── */
export const dayKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
export const todayKey = () => dayKey(new Date())

// Anchors are always the 1st of a month at local midnight, so month maths never
// trips over a 31st landing in a short month.
export const thisMonth = () => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), 1) }
export const shiftMonth = (anchor, delta) => new Date(anchor.getFullYear(), anchor.getMonth() + delta, 1)
export const isSameMonth = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth()

// Inclusive ISO bounds for the read — Xano's timetable query compares against
// `appointment.date`, which is a plain Bangkok date string.
export function monthRange(anchor) {
  const y = anchor.getFullYear(); const m = anchor.getMonth()
  return { start: `${y}-${pad(m + 1)}-01`, end: `${y}-${pad(m + 1)}-${pad(new Date(y, m + 1, 0).getDate())}` }
}

export const fmtMonthTH = (anchor) => `${TH_MONTHS_FULL[anchor.getMonth()]} ${anchor.getFullYear() + 543}`

// "จันทร์ 9 ส.ค. 2569" — the day panel's title.
export function fmtDayTH(key) {
  const d = parseDate(key)
  if (!d) return '—'
  return `${TH_DAYS_FULL[d.getDay()]} ${d.getDate()} ${TH_MONTHS_ABBR[d.getMonth()]} ${d.getFullYear() + 543}`
}

/* Cells for a Monday-first month grid: leading/trailing nulls pad the weeks so
 * the caller can render a flat 7-column list without index arithmetic. */
export function monthCells(anchor) {
  const y = anchor.getFullYear(); const m = anchor.getMonth()
  const lead = (new Date(y, m, 1).getDay() + 6) % 7
  const days = new Date(y, m + 1, 0).getDate()
  const cells = []
  for (let i = 0; i < lead; i++) cells.push(null)
  for (let d = 1; d <= days; d++) cells.push(`${y}-${pad(m + 1)}-${pad(d)}`)
  while (cells.length % 7) cells.push(null)
  return cells
}

/* ── timestamps ─────────────────────────────────────────────────────────── */
const ts = (v) => { const n = Number(v); return Number.isFinite(n) && n > 0 ? n : null }

export const startTs = (r) => ts(r?.start_date)
export const endTs = (r) => ts(r?.end_date)

export function fmtTime(v) {
  const n = ts(v)
  if (n == null) return '—'
  const d = new Date(n)
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export function tsToTimeInput(v) {
  const n = ts(v)
  if (n == null) return ''
  const d = new Date(n)
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`
}

// Anchor a typed "HH:mm" onto a 'YYYY-MM-DD' day. The date half is never taken
// from the timestamp being replaced — a shift belongs to the day it is filed on.
export function timeOnDay(key, hhmm) {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(hhmm || '').trim())
  const d = parseDate(key)
  if (!m || !d) return null
  d.setHours(Number(m[1]), Number(m[2]), 0, 0)
  return d.getTime()
}

export const durationMin = (start, end) => (
  start != null && end != null && end > start ? Math.round((end - start) / 60000) : 0
)

export function fmtDuration(mins) {
  const n = Number(mins) || 0
  if (n <= 0) return '—'
  const h = Math.floor(n / 60); const m = n % 60
  if (!h) return `${m} นาที`
  return m ? `${h} ชม. ${m} นาที` : `${h} ชม.`
}

/* ── the branch's hours → a doctor's default window ──────────────────────
 * `Branch` carries the STAFF hours (clockin_hour/min → clockout_hour/min). A
 * doctor's shift is not those hours: the counter opens 45 minutes before the
 * first patient can be seated (setup + ประชุมเช้า), so a new shift starts 45
 * minutes AFTER the branch's clock-in and ends exactly ON its clock-out — no
 * padding at the end, because the last slot is booked up to closing.
 * e.g. อโศก 09:15–20:00 → 10:00–20:00 · รัชโยธิน 09:45–20:30 → 10:30–20:30. */
export const DOCTOR_START_OFFSET_MIN = 45

// Used only when a branch record has no hours at all — better than an empty
// time input, and the operator can still type over it.
export const FALLBACK_WINDOW = { start: '10:00', end: '20:00' }

const hhmm = (mins) => `${pad(Math.floor(((mins % 1440) + 1440) % 1440 / 60))}:${pad(((mins % 60) + 60) % 60)}`

export function branchWindow(branch) {
  const h = Number(branch?.clockin_hour); const m = Number(branch?.clockin_min)
  const H = Number(branch?.clockout_hour); const M = Number(branch?.clockout_min)
  if (!Number.isFinite(h) || !Number.isFinite(H)) return { ...FALLBACK_WINDOW }
  const start = h * 60 + (Number.isFinite(m) ? m : 0) + DOCTOR_START_OFFSET_MIN
  const end = H * 60 + (Number.isFinite(M) ? M : 0)
  if (end <= start) return { ...FALLBACK_WINDOW }
  return { start: hhmm(start), end: hhmm(end) }
}

export const findBranch = (branches, id) => (
  (Array.isArray(branches) ? branches : []).find((b) => String(b.id) === String(id)) || null
)

/* ── rows ───────────────────────────────────────────────────────────────── */
export const asList = (r) => (Array.isArray(r) ? r : (r?.items || []))

// The feed is already scoped to shift rows server-side; this is the belt to that
// braces, so a widened query can never make patient bookings editable here.
export const isShiftRow = (r) => (
  String(r?.event_type || '') === SHIFT_EVENT_TYPE && r?.block !== true && !Number(r?.patient_id)
)

// The row's own `date` is authoritative (Bubble writes both); derive only when a
// row somehow arrives without one.
export function rowDayKey(r) {
  const d = String(r?.date || '').slice(0, 10)
  if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return d
  const t = startTs(r)
  return t == null ? '' : dayKey(new Date(t))
}

// Display name, cheapest source first: the addon, then the denormalised column
// Bubble keeps on the row, then the doctor list this page already loaded.
export function doctorLabel(r, byId) {
  const fromAddon = r?.doctor?.code
  if (fromAddon) return fromAddon
  if (r?.titleDoctorTB) return r.titleDoctorTB
  const d = byId?.get?.(Number(r?.doctor_id))
  if (d) return d.code || [d.nickname, d.name].filter(Boolean).join(' ')
  return `หมอ #${r?.doctor_id ?? '—'}`
}

// Bubble's own convention for the denormalised label: "<ชื่อเล่น> <ชื่อจริง>",
// which is exactly what Doctor.code already holds.
export const doctorTitleOf = (d) => d?.code || [d?.nickname, d?.name].filter(Boolean).join(' ').trim()

export function groupByDay(rows, { doctorId } = {}) {
  const map = new Map()
  for (const r of rows) {
    if (!isShiftRow(r)) continue
    if (doctorId && String(r.doctor_id) !== String(doctorId)) continue
    const k = rowDayKey(r)
    if (!k) continue
    if (!map.has(k)) map.set(k, [])
    map.get(k).push(r)
  }
  // Earliest window first, then by doctor name — a day cell should read as the
  // running order of the clinic, not as insertion order.
  for (const list of map.values()) {
    list.sort((a, b) => (startTs(a) ?? 0) - (startTs(b) ?? 0)
      || String(a.titleDoctorTB || '').localeCompare(String(b.titleDoctorTB || ''), 'th'))
  }
  return map
}

/* ── colour ─────────────────────────────────────────────────────────────
 * `event_color` is no longer chosen in the UI — every shift this drawer writes
 * carries DEFAULT_EVENT_COLOR — but the roster still holds legacy rows that a
 * human coloured in Bubble, so the month grid keeps honouring them. Free text on
 * the row, therefore validated before it is ever put in a style attribute. */
export function normHex(v) {
  const s = String(v || '').trim()
  return /^#[0-9a-fA-F]{6}$/.test(s) ? s.toUpperCase() : ''
}

export const isCustomColor = (v) => {
  const hex = normHex(v)
  return !!hex && hex !== DEFAULT_EVENT_COLOR.toUpperCase()
}

// 8-digit hex = the same hue at low alpha, so the chip stays readable on white
// whatever the doctor picked.
export function chipStyle(color) {
  const hex = normHex(color)
  if (!hex || hex === DEFAULT_EVENT_COLOR.toUpperCase()) return null
  return { backgroundColor: `${hex}26`, borderColor: `${hex}80` }
}

/* ── per-shift spec / except override ────────────────────────────────────
 * `custom_spec` (appointmentList TYPE strings) and `custom_except`
 * (appointmentList IDS) live on the shift row and OVERRIDE the doctor's own
 * `spec` / `except` for that shift — see lib/doctorFilter.js#shiftEligible,
 * which every booking screen filters through. The two travel together: an
 * override that set only `custom_spec` would silently inherit the doctor's
 * default exceptions, which is not what "this shift is different" means.
 *
 * The timetable feed returns the raw columns, but the doctor LIST resolves
 * `except` through an addon that outputs only `{detail}`, so both shapes are
 * accepted on the way in and only ids ever go out. */
export function customExceptIds(raw, apptByDetail) {
  return (Array.isArray(raw) ? raw : []).map((e) => {
    if (e && typeof e === 'object') return Number(apptByDetail?.get?.(e.detail)?.id) || 0
    return Number(e) || 0
  }).filter(Boolean)
}

export const hasCustomSpec = (r) => Array.isArray(r?.custom_spec) && r.custom_spec.filter(Boolean).length > 0

// What this shift actually accepts, whichever side of the override it is on.
export function effectiveSpec(row, doctor) {
  return hasCustomSpec(row)
    ? row.custom_spec.filter(Boolean).map(String)
    : (Array.isArray(doctor?.spec) ? doctor.spec.filter(Boolean).map(String) : [])
}

/* ── form ⇄ row ─────────────────────────────────────────────────────────── */
export function blankShift(key, { branchId = '', doctorId = '', branch = null } = {}) {
  const w = branchWindow(branch)
  return {
    id: null,
    doctor_id: doctorId ? String(doctorId) : '',
    branch_id: branchId ? String(branchId) : '',
    date: key || todayKey(),
    start: w.start,
    end: w.end,
    details: '',
    custom_on: false,
    custom_spec: [],
    custom_except: [],
  }
}

export function shiftToForm(r, { apptByDetail } = {}) {
  // A legacy all-day row has a real 00:00–23:59 window on it, so it opens as
  // those times rather than as a mode this drawer no longer offers.
  const w = r?.all_day === true ? { start: '00:00', end: '23:59' } : {
    start: tsToTimeInput(startTs(r)), end: tsToTimeInput(endTs(r)),
  }
  return {
    id: r?.id ?? null,
    doctor_id: r?.doctor_id ? String(r.doctor_id) : '',
    branch_id: r?.branch_id ? String(r.branch_id) : '',
    date: rowDayKey(r) || todayKey(),
    start: w.start,
    end: w.end,
    details: r?.details || '',
    custom_on: hasCustomSpec(r),
    custom_spec: hasCustomSpec(r) ? r.custom_spec.filter(Boolean).map(String) : [],
    custom_except: customExceptIds(r?.custom_except, apptByDetail),
  }
}

// Returns { payload } or { error } — one place decides what a valid shift is, so
// the drawer never has to re-derive the window it is about to send.
export function shiftPayload(form, { doctorTitle } = {}) {
  const doctorId = Number(form.doctor_id) || 0
  const branchId = Number(form.branch_id) || 0
  if (!doctorId) return { error: 'กรุณาเลือกทันตแพทย์' }
  if (!branchId) return { error: 'กรุณาเลือกสาขา' }
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(form.date || ''))) return { error: 'กรุณาเลือกวันที่' }

  const start = timeOnDay(form.date, form.start)
  const end = timeOnDay(form.date, form.end)
  if (start == null) return { error: 'กรุณาระบุเวลาเริ่มเวร' }
  if (end == null) return { error: 'กรุณาระบุเวลาสิ้นสุดเวร' }
  // A shift that ends before it starts is a typo, never an overnight clinic —
  // no branch here opens past midnight, so rolling it forward would silently
  // create a 20-hour window.
  if (end <= start) return { error: 'เวลาสิ้นสุดต้องอยู่หลังเวลาเริ่ม' }

  const custom = form.custom_on === true
  const spec = custom ? [...new Set((form.custom_spec || []).filter(Boolean).map(String))] : []
  if (custom && !spec.length) {
    return { error: 'กำหนดเองแล้วต้องเลือกความเชี่ยวชาญอย่างน้อย 1 รายการ (หรือกลับไปใช้ค่าเริ่มต้นของหมอ)' }
  }

  return {
    payload: {
      doctor_id: doctorId,
      branch_id: branchId,
      date: form.date,
      start_date: start,
      end_date: end,
      duration: durationMin(start, end),
      // The drawer offers only a real window, so every row it writes is a real
      // window. Kept in the payload (rather than dropped) so opening a legacy
      // all-day row and saving it converts the row instead of leaving a flag
      // whose UI no longer exists.
      all_day: false,
      event_color: DEFAULT_EVENT_COLOR,
      details: String(form.details || '').trim(),
      titleDoctorTB: doctorTitle || '',
      custom_spec: spec,
      custom_except: custom ? [...new Set((form.custom_except || []).map(Number).filter(Boolean))] : [],
    },
  }
}

// Order-insensitive: Xano hands the lists back in its own order, and a reshuffle
// is not an edit.
const sameList = (a, b) => {
  const x = (Array.isArray(a) ? a : []).map(String).sort()
  const y = (Array.isArray(b) ? b : []).map(String).sort()
  return x.length === y.length && x.every((v, i) => v === y[i])
}

// Only the keys that actually moved — PATCH /appointment is a true sparse patch,
// so sending the untouched ones would stamp modified_at for nothing and widen
// the blast radius of a bug in this form.
export function changedFields(payload, row) {
  const before = {
    doctor_id: Number(row?.doctor_id) || 0,
    branch_id: Number(row?.branch_id) || 0,
    date: rowDayKey(row),
    start_date: startTs(row) ?? 0,
    end_date: endTs(row) ?? 0,
    duration: Number(row?.duration) || 0,
    all_day: row?.all_day === true,
    event_color: normHex(row?.event_color) || DEFAULT_EVENT_COLOR,
    details: row?.details || '',
    titleDoctorTB: row?.titleDoctorTB || '',
    custom_spec: Array.isArray(row?.custom_spec) ? row.custom_spec.filter(Boolean) : [],
    custom_except: customExceptIds(row?.custom_except),
  }
  const out = {}
  for (const [k, v] of Object.entries(payload)) {
    if (Array.isArray(v)) { if (!sameList(v, before[k])) out[k] = v }
    else if (before[k] !== v) out[k] = v
  }
  return out
}
